import type { ProgressEvent } from '@sae/engine';

/**
 * Staged run progress (2026-10-18 redesign): a stage checklist built
 * ONLY from the engine's own progress events — the stages it reports
 * (direct estimates, design effects, one event per fitted cell,
 * disclosure checks) and its own step counts. The pipeline exposes
 * no bootstrap replicate counts, so none are shown; the fitting
 * stage's detail is the engine's step position, exactly as reported.
 * An unrecognized stage key renders under its raw key, as the
 * previous single-line progress display did.
 */
const STAGE_ORDER: readonly string[] = [
  'direct',
  'design-effects',
  'cell',
  'disclosure',
];

const STAGE_LABELS: Readonly<Record<string, string>> = {
  direct: 'Direct estimates',
  'design-effects': 'Design effects',
  cell: 'Model fitting',
  disclosure: 'Disclosure checks',
};

export function RunProgress({
  events,
}: {
  events: readonly ProgressEvent[];
}) {
  if (events.length === 0) return null;
  const latest = events[events.length - 1];
  const seen = new Set(events.map((e) => e.stage));
  const unknown = [...seen].filter((s) => !STAGE_ORDER.includes(s));
  const stages = [...STAGE_ORDER, ...unknown];
  const pct = Math.round(
    (latest.completed / Math.max(1, latest.total)) * 100,
  );

  return (
    <div className="progress" role="status" aria-live="polite">
      <div className="meter meter-run">
        <i style={{ width: `${pct}%` }}></i>
      </div>
      <ol className="run-stages">
        {stages.map((stage) => {
          const state =
            stage === latest.stage
              ? 'active'
              : seen.has(stage)
                ? 'done'
                : 'pending';
          return (
            <li key={stage} className={state}>
              <span className="rs-dot" aria-hidden="true">
                {state === 'done' ? '✓' : ''}
              </span>
              <span>
                {STAGE_LABELS[stage] ?? stage}
                {state === 'active' && (
                  <span className="rs-detail">
                    {' '}
                    — step {latest.completed} of {latest.total}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
